import { useState } from 'react';
import { getTeamLogoUrl, teamLogoSize } from '@/shared/services/getTeamLogoUrl';
import { Box, CircularProgress, Fade, Grow, Stack, Typography, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { GameModel } from '../types';

const SLATE_TRANSITION = { enter: 300, exit: 400 };

const GameSplash = ({
  open,
  game,
  message,
}: {
  open: boolean;
  game: GameModel;
  message: string;
}) => {
  const theme = useTheme();
  // Keep the last message through the fade-out: a recovered stall swaps it back
  // to the loading copy in the same render that closes the splash.
  const [shownMessage, setShownMessage] = useState(message);
  if (open && message !== shownMessage) setShownMessage(message);
  const { homeTeam, awayTeam } = game;

  if (!homeTeam || !awayTeam) return null;

  const awayLogoPath = getTeamLogoUrl(awayTeam);
  const homeLogoPath = getTeamLogoUrl(homeTeam);

  // Six-digit fallback: the gradient appends a `99` alpha to these.
  const homePrimaryTeamColor =
    game.homeTeamColor ||
    homeTeam.teamColors?.find((c) => c.type === 'primary')?.hex_color ||
    '#000000';

  const awayPrimaryTeamColor =
    game.awayTeamColor ||
    awayTeam.teamColors?.find((c) => c.type === 'primary')?.hex_color ||
    '#000000';

  return (
    <Fade in={open} timeout={SLATE_TRANSITION} unmountOnExit>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 10,
          background: `linear-gradient(110deg,
          ${awayPrimaryTeamColor}99 0%,
          black 50%,
          ${homePrimaryTeamColor}99 100%
        )`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: theme.spacing(4),
        }}
      >
        {/* Main content container */}
        <Grow in={open} timeout={SLATE_TRANSITION}>
          <Stack
            direction="column"
            spacing={4}
            sx={{
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {/* Teams matchup section, venue underneath */}
            <Stack
              spacing={2}
              sx={{
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
              }}
            >
              <Stack
                direction="row"
                sx={{
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {/* Away Team */}
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{
                    flex: 1,
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      minWidth: '192px',
                      height: '192px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={awayLogoPath}
                      alt={awayTeam.abbreviation}
                      style={teamLogoSize(192)}
                      loading="lazy"
                    />
                  </Box>
                  <Typography
                    variant="h2"
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  >
                    {awayTeam.abbreviation}
                  </Typography>
                </Stack>

                {/* VS Divider */}
                <Box sx={{ px: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: grey[500],
                      fontWeight: 600,
                    }}
                  >
                    VS
                  </Typography>
                </Box>

                {/* Home Team */}
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{
                    flex: 1,
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  >
                    {homeTeam.abbreviation}
                  </Typography>
                  <Box
                    sx={{
                      minWidth: '192px',
                      height: '192px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={homeLogoPath}
                      alt={homeTeam.abbreviation}
                      style={teamLogoSize(192)}
                      loading="lazy"
                    />
                  </Box>
                </Stack>
              </Stack>

              <Typography
                variant="subtitle1"
                sx={{
                  color: grey[400],
                }}
              >
                {game.venue?.name} - {game.venue?.city}
              </Typography>
            </Stack>

            {/* Message section */}
            <Stack spacing={2} sx={{ textAlign: 'center', alignItems: 'center', mb: 8 }}>
              <CircularProgress size={40} sx={{ color: grey[100] }} />
              <Typography
                variant="h6"
                sx={{
                  color: grey[100],
                  fontWeight: 600,
                }}
              >
                {shownMessage}
              </Typography>
            </Stack>
          </Stack>
        </Grow>
      </Box>
    </Fade>
  );
};

export default GameSplash;
